import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet'; // Import Helmet for SEO
import { ArrowRight, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const HeroPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // High-Quality Images
  const slides = [
    {
      img: "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?q=80&w=2074&auto=format&fit=crop",
      title: "Royal Living",
      sub: "Where Elegance Meets Comfort"
    },
    {
      img: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=2053&auto=format&fit=crop",
      title: "Timeless Design",
      sub: "Crafting Legacy in Every Corner"
    },
    {
      img: "https://images.unsplash.com/photo-1631679706909-1844bbd07221?q=80&w=1992&auto=format&fit=crop",
      title: "Modern Heritage",
      sub: "A Symphony of Gold & Velvet"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  // Schema Markup for the Homepage/Brand
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Luxuriya Homes",
    "url": "https://www.luxuriyahomes.com",
    "logo": "https://www.luxuriyahomes.com/logo.png", // Replace with your actual logo URL
    "description": "Luxuriya Homes specializes in premium interior design, transforming homes into royal sanctuaries with timeless elegance.",
    "sameAs": [
      "https://www.instagram.com/luxuriyahomes",
      "https://www.facebook.com/luxuriyahomes"
    ]
  };

  return (
    <div className="bg-white text-black font-sans selection:bg-amber-200 selection:text-black overflow-x-hidden">
      
      {/* --- SEO HEAD SECTION --- */}
      <Helmet>
        <title>Luxuriya Homes | Premium Interior Design & Royal Renovations</title>
        <meta name="description" content="Transform your home with Luxuriya Homes. We specialize in luxury interior design, offering royal living themes, gold accents, and timeless heritage renovations." />
        <meta name="keywords" content="luxury interior design, royal homes, premium home renovation, timeless interior, gold and velvet decor" />
        <link rel="canonical" href="https://www.luxuriyahomes.com/" />
        
        {/* Open Graph for Social Media Sharing */}
        <meta property="og:title" content="Luxuriya Homes - Where Elegance Meets Comfort" />
        <meta property="og:description" content="Crafting legacy in every corner. Explore our premium interior design portfolio." />
        <meta property="og:image" content={slides[0].img} />
        <meta property="og:type" content="website" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>

      {/* 1. HERO SLIDER SECTION */}
      {/* Height increased to 92vh for a taller, more cinematic look */}
      <section className="relative h-[92vh] w-full overflow-hidden bg-gray-900" aria-label="Hero Slider">
        
        {/* Background Images */}
        {slides.map((slide, index) => (
          <div 
            key={index}
            className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            <img 
              src={slide.img} 
              alt={`${slide.title} - Luxury Interior Design Style`} // Optimized Alt Text
              fetchPriority={index === 0 ? "high" : "auto"} // Performance: Load first image faster
              className={`w-full h-full object-cover ${index === currentSlide ? 'animate-subtle-zoom' : ''}`}
            />
            {/* Base Overlay */}
            <div className="absolute inset-0 bg-black/20"></div>
          </div>
        ))}

        {/* --- NEW ADDITION: Top & Bottom Gradients --- */}
        {/* Top Gradient (Navbar area darker) */}
        <div className="absolute top-0 left-0 w-full h-48 bg-gradient-to-b from-black/90 via-black/40 to-transparent z-10 pointer-events-none"></div>
        
        {/* Bottom Gradient (Smooth transition to next section) */}
        <div className="absolute bottom-0 left-0 w-full h-48 bg-gradient-to-t from-black/90 via-black/40 to-transparent z-10 pointer-events-none"></div>


        {/* Hero Content - Perfectly Centered */}
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center px-4">
           <div className="animate-fade-in-up mt-10">
              <p className="text-amber-300 tracking-[0.4em] uppercase text-xs md:text-sm mb-6 font-bold shadow-black drop-shadow-md">
                 {slides[currentSlide].sub}
              </p>
              {/* Main H1 Title - Crucial for SEO */}
              <h1 className="text-5xl md:text-8xl font-serif text-white mb-10 drop-shadow-2xl">
                {slides[currentSlide].title}
              </h1>
              <Link 
                to="/projects" 
                className="inline-block px-10 py-4 bg-white/10 border border-white/50 text-white hover:bg-white hover:text-black hover:border-white transition-all duration-300 uppercase tracking-widest text-xs backdrop-blur-md"
                aria-label="Explore our luxury design collection"
              >
                Explore Collection
              </Link>
           </div>
        </div>

        {/* Slide Indicators (Dots) */}
        <div className="absolute bottom-12 right-8 z-30 flex gap-3">
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              aria-label={`Go to slide ${idx + 1}`} // Accessibility
              className={`h-1.5 rounded-full transition-all duration-300 ${
                currentSlide === idx ? 'w-8 bg-amber-400' : 'w-2 bg-white/40 hover:bg-white'
              }`}
            />
          ))}
        </div>
      </section>

      {/* 2. FLOATING INFO BAR */}
      <div className="relative z-30 -mt-16 max-w-5xl mx-auto px-4">
         <div className="bg-white shadow-[0_20px_50px_rgba(0,0,0,0.3)] flex flex-col md:flex-row justify-between items-center p-8 rounded-sm border-t-4 border-amber-600">
            <div className="mb-6 md:mb-0 text-center md:text-left">
               <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">Total Projects</p>
               <p className="text-2xl font-serif text-gray-900">150+ <span className="text-base text-gray-500 font-sans">Completed</span></p>
            </div>
            
            <div className="hidden md:block h-10 w-[1px] bg-gray-200"></div>
            
            <div className="mb-6 md:mb-0 text-center md:text-left">
               <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">Recognition</p>
               <div className="flex items-center gap-2 justify-center md:justify-start">
                 <Star size={18} className="text-amber-500 fill-amber-500"/>
                 <p className="text-lg font-serif text-gray-900">Best Luxury Design 2024</p>
              </div>
            </div>
            
            <div className="hidden md:block h-10 w-[1px] bg-gray-200"></div>
            
            <Link to="/contact" className="px-8 py-3 bg-black text-white text-xs uppercase tracking-widest hover:bg-amber-700 transition-colors shadow-lg">
               Start Your Journey
            </Link>
         </div>
      </div>

    </div>
  );
};

export default HeroPage;